import { useMemo, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { InclusionChips } from "@/pages/InclusionsPage";

export default function MarkSentDialog({ open, onOpenChange, joinerId, drops, onDone }) {
  const eligible = drops.filter((g) => Object.keys(g.owed).length);
  const [picked, setPicked] = useState([]);
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const preview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const submit = async () => {
    if (!picked.length) return toast.error("Select at least one drop round");
    const fd = new FormData();
    fd.append("joiner_id", joinerId);
    picked.forEach((d) => fd.append("drop_ids", d));
    fd.append("note", note);
    if (file) fd.append("file", file);
    setBusy(true);
    try {
      await api.post("/inclusion-shipments", fd);
      toast.success("Bundle marked as sent");
      setPicked([]); setNote(""); setFile(null);
      onDone();
    } catch (e) {
      toast.error(errMsg(e, "Couldn't mark bundle as sent"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-h-[90vh] overflow-y-auto" data-testid="mark-sent-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Mark inclusions sent</DialogTitle>
          <DialogDescription>Tick the drop rounds included in this bundle. Their paid inclusions are taken off what's owed.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2" data-testid="mark-sent-drop-list">
          {eligible.length === 0 && <p className="text-sm text-zinc-500">No drop rounds with owed inclusions left.</p>}
          {eligible.map((g) => (
            <label key={g.drop_id} className="flex items-start gap-3 rounded-xl border border-zinc-800 p-3 cursor-pointer hover:border-pink-500/40 transition-colors">
              <Checkbox data-testid={`mark-sent-drop-${g.drop_id}`} checked={picked.includes(g.drop_id)} onCheckedChange={() => toggle(g.drop_id)} className="mt-0.5" />
              <div className="space-y-1.5 min-w-0">
                <div className="text-sm font-medium">{g.drop_title}</div>
                <InclusionChips totals={g.owed} testPrefix={`mark-sent-total-${g.drop_id}`} />
              </div>
            </label>
          ))}
        </div>
        <div className="space-y-1.5">
          <Label>Bundle photo (optional)</Label>
          <label className="flex items-center gap-3 rounded-xl border border-dashed border-zinc-700 p-3 cursor-pointer hover:border-pink-500/40 transition-colors">
            {preview ? <img src={preview} alt="Bundle" className="h-16 w-16 rounded-lg object-cover" /> : <ImagePlus className="h-6 w-6 text-zinc-500" />}
            <span className="text-sm text-zinc-400 truncate">{file ? file.name : "Upload a photo of the inclusions"}</span>
            <input type="file" accept="image/*" className="hidden" data-testid="mark-sent-photo-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="bundle-note">Note (optional)</Label>
          <Textarea id="bundle-note" data-testid="mark-sent-note-input" value={note} onChange={(e) => setNote(e.target.value)} className="bg-zinc-900" rows={2} />
        </div>
        <Button data-testid="mark-sent-submit" disabled={busy || !eligible.length} onClick={submit} className="rounded-full bg-pink-500 hover:bg-pink-400 text-white">
          {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Mark as sent
        </Button>
      </DialogContent>
    </Dialog>
  );
}
